import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";

export default function HeroCarousel({
    slides = [],
    height = "500px",
    autoPlay = true,
    autoPlayInterval = 5000,
    showDots = true,
    showArrows = true,
    className = ""
}) {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (!autoPlay) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % slides.length);
        }, autoPlayInterval);

        return () => clearInterval(interval);
    }, [autoPlay, autoPlayInterval, slides.length]);

    const goToPrevious = () => {
        setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
    };

    const goToNext = () => {
        setCurrentIndex((prev) => (prev + 1) % slides.length);
    };

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    const currentSlide = slides[currentIndex];

    return (
        <div className={`relative w-full overflow-hidden ${className}`} style={{ height }}>
            {/* Slide */}
            <Link href={currentSlide.link || '#'}>
                <div className="relative w-full h-full">
                    {/* Background Image */}
                    <Image
                        src={currentSlide.image}
                        alt={currentSlide.heading}
                        fill
                        className="object-cover"
                        priority
                    />
                </div>
            </Link>

            {/* Navigation Arrows */}
            {showArrows && slides.length > 1 && (
                <>
                    {/* Left Arrow */}
                    <button
                        onClick={goToPrevious}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-1 md:p-3 transition-all group"
                    >
                        <BiChevronLeft className="text-md md:text-4xl text-white" />
                    </button>

                    {/* Right Arrow */}
                    <button
                        onClick={goToNext}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-1 md:p-3 transition-all group"
                        aria-label="Next slide"
                    >
                        <BiChevronRight className="text-md md:text-4xl text-white" />
                    </button>
                </>
            )}

            {/* Dots Indicator */}
            {showDots && slides.length > 1 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2">
                    {slides.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`transition-all rounded-full ${index === currentIndex
                                ? 'bg-white w-8 h-2'
                                : 'bg-white/50 hover:bg-white/70 w-2 h-2'
                                }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
